import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { FaFileCircleCheck, FaFileInvoiceDollar, FaFilePen, FaFileShield } from 'react-icons/fa6';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import { ActionIcon, Button, Checkbox, Input, Popover, Text, Textarea } from 'rizzui';
import { toast } from 'sonner';
import ModalForm from '../../../../components/forms/ModalForm';
import useHandleErrors from '../../../../hooks/useHandleErrors';
import { InvoiceService } from '../../../../services/invoice/invoiceService';
import { Invoice } from '../../../../type/entities/invoice-type';
import { fixNumber } from '../../../../utils/formatNumber';
import {
    buildReplacementDataFromInvoice,
    getCreditNotePreviewAmount,
    RectificationAction,
    rectificationActionRequiresAmount,
    rectificationActionRequiresPreview,
} from './InvoiceRectificationModal.helpers';

type InvoiceRectificationModalProps = {
    isOpen: boolean;
    invoice: Invoice | null | undefined;
    onClose: () => void;
    onCompleted?: () => void;
};

type AmountSource = 'manual' | 'expirations';

type ExpirationPreviewStatus = {
    id: string;
    name: string;
    color?: string;
};

type AffectedExpirationPreview = {
    id: string;
    description?: string;
    expirationDate?: string;
    paymentDate?: string | null;
    reminderDate?: string | null;
    currentDueStatusDate?: string | null;
    currentDueManagementStatusDate?: string | null;
    amount?: number;
    currentAmount?: number;
    appliedAmount: number;
    remainingAmount: number;
    isPaid?: boolean | null;
    isRemitted?: boolean;
    paymentMethod?: {
        id: string;
        name: string;
    } | null;
    currentDueStatus?: ExpirationPreviewStatus | null;
    currentDueManagementStatus?: ExpirationPreviewStatus | null;
    reminderResponsible?: {
        id: string;
        name: string;
        email?: string;
    } | null;
    notes?: string | null;
    status?: string;
};

type CreditNotePreview = {
    maxRectifiableAmount?: number;
    pendingExpirationAmount?: number;
    alreadyRectifiedAmount?: number;
    creditNote?: {
        taxableAmount?: number;
        totalTaxesAmount?: number;
        totalPrice?: number;
    };
    invoiceExpirations?: AffectedExpirationPreview[];
    pendingExpirations?: AffectedExpirationPreview[];
    affectedExpirations?: AffectedExpirationPreview[];
};

const ACTIONS: Array<{
    value: RectificationAction;
    label: string;
    description: string;
    icon: JSX.Element;
}> = [
    {
        value: 'minor_regeneration',
        label: 'Regenerar',
        description: 'Solo PDF, sin abono',
        icon: <FaFilePen className="h-4 w-4" />,
    },
    {
        value: 'full_credit_note',
        label: 'Abono completo',
        description: 'Anula el importe pendiente',
        icon: <FaFileShield className="h-4 w-4" />,
    },
    {
        value: 'partial_credit_note',
        label: 'Abono parcial',
        description: 'Abona un importe concreto',
        icon: <FaFileInvoiceDollar className="h-4 w-4" />,
    },
    {
        value: 'major_replacement',
        label: 'Abono + sustitutiva',
        description: 'Crea nueva factura corregida',
        icon: <FaFileCircleCheck className="h-4 w-4" />,
    },
];

const getApiPayload = (responseData: any) => responseData?.data ?? responseData;

const formatPreviewDate = (date?: string | null) => {
    return date ? moment(date).format('DD/MM/YYYY') : '-';
};

const getExpirationAmount = (expiration: AffectedExpirationPreview) => {
    return expiration.currentAmount ?? expiration.amount ?? 0;
};

const isExpirationPaid = (expiration: AffectedExpirationPreview) => expiration.isPaid === true;

const getExpirationPaymentLabel = (expiration: AffectedExpirationPreview) => (
    isExpirationPaid(expiration) ? 'Pagado' : 'No pagado'
);

const getExpirationSelectionRowClassName = (expiration: AffectedExpirationPreview) => {
    const baseClassName = 'flex items-start gap-3 border-b px-3 py-3 last:border-b-0 transition';

    if (isExpirationPaid(expiration)) {
        return `${baseClassName} cursor-default border-green-100 bg-gradient-to-r from-emerald-50 to-green-100/70`;
    }

    return `${baseClassName} cursor-pointer border-orange-100 bg-gradient-to-r from-orange-50 to-amber-100/70 hover:from-orange-100 hover:to-amber-100`;
};

const getExpirationPaymentBadgeClassName = (expiration: AffectedExpirationPreview) => (
    isExpirationPaid(expiration)
        ? 'border-green-200 bg-green-50 text-green-700'
        : 'border-orange-200 bg-orange-50 text-orange-700'
);

const getPreviewExpirations = (payload: CreditNotePreview | null | undefined) => (
    payload?.invoiceExpirations ?? payload?.pendingExpirations ?? []
);

const ExpirationDetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="grid grid-cols-[130px_1fr] gap-2 border-b border-gray-100 py-2 last:border-b-0">
        <Text className="text-xs font-semibold uppercase text-gray-500">{label}</Text>
        <div className="text-sm text-gray-800">{value || '-'}</div>
    </div>
);

const ExpirationStatus = ({ status }: { status?: ExpirationPreviewStatus | null }) => {
    if (!status) return <>-</>;

    return (
        <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: status.color || '#9ca3af' }} />
            {status.name}
        </span>
    );
};

const ExpirationInfoPopover = ({ expiration }: { expiration: AffectedExpirationPreview }) => (
    <Popover placement="left" overlayClassName="z-[1100]">
        <Popover.Trigger>
            <ActionIcon
                size="sm"
                variant="text"
                aria-label="Ver detalle del vencimiento"
                className="shrink-0 text-gray-500 hover:text-primary"
            >
                <HiOutlineInformationCircle className="h-5 w-5" />
            </ActionIcon>
        </Popover.Trigger>
        <Popover.Content className="z-[1100]">
            <div className="w-[360px] max-w-[calc(100vw-32px)] p-1">
                <Text className="mb-2 font-semibold text-gray-900">
                    {expiration.description || expiration.id}
                </Text>
                <div className="rounded-md border border-gray-100 px-3">
                    <ExpirationDetailRow label="ID" value={<span className="break-all">{expiration.id}</span>} />
                    <ExpirationDetailRow label="Importe" value={`${fixNumber(getExpirationAmount(expiration))} €`} />
                    <ExpirationDetailRow label="Aplicado" value={`${fixNumber(expiration.appliedAmount)} €`} />
                    <ExpirationDetailRow label="Restante" value={`${fixNumber(expiration.remainingAmount)} €`} />
                    <ExpirationDetailRow label="Vencimiento" value={formatPreviewDate(expiration.expirationDate)} />
                    <ExpirationDetailRow label="Pago" value={formatPreviewDate(expiration.paymentDate)} />
                    <ExpirationDetailRow label="Método" value={expiration.paymentMethod?.name || '-'} />
                    <ExpirationDetailRow label="Estado" value={<ExpirationStatus status={expiration.currentDueStatus} />} />
                    <ExpirationDetailRow label="Fecha estado" value={formatPreviewDate(expiration.currentDueStatusDate)} />
                    <ExpirationDetailRow label="Gestión" value={<ExpirationStatus status={expiration.currentDueManagementStatus} />} />
                    <ExpirationDetailRow label="Fecha gestión" value={formatPreviewDate(expiration.currentDueManagementStatusDate)} />
                    <ExpirationDetailRow label="Pagado" value={expiration.isPaid ? 'Sí' : 'No'} />
                    <ExpirationDetailRow label="Remesado" value={expiration.isRemitted ? 'Sí' : 'No'} />
                    <ExpirationDetailRow label="Recordatorio" value={formatPreviewDate(expiration.reminderDate)} />
                    <ExpirationDetailRow label="Responsable" value={expiration.reminderResponsible?.name || '-'} />
                    <ExpirationDetailRow label="Email" value={expiration.reminderResponsible?.email || '-'} />
                    <ExpirationDetailRow label="Notas" value={expiration.notes || '-'} />
                </div>
            </div>
        </Popover.Content>
    </Popover>
);

const InvoiceRectificationModal: React.FC<InvoiceRectificationModalProps> = ({ isOpen, invoice, onClose, onCompleted }) => {
    const service = useMemo(() => new InvoiceService(), []);
    const { handleErrors } = useHandleErrors();
    const [selectedAction, setSelectedAction] = useState<RectificationAction>('minor_regeneration');
    const [amountSource, setAmountSource] = useState<AmountSource>('manual');
    const [reason, setReason] = useState('');
    const [amount, setAmount] = useState('');
    const [replacementTotalPrice, setReplacementTotalPrice] = useState('');
    const [selectedExpirationIds, setSelectedExpirationIds] = useState<string[]>([]);
    const [invoiceExpirations, setInvoiceExpirations] = useState<AffectedExpirationPreview[]>([]);
    const [operationDate, setOperationDate] = useState(moment().format('YYYY-MM-DD'));
    const [preview, setPreview] = useState<CreditNotePreview | null>(null);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [isPendingExpirationsLoading, setIsPendingExpirationsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const requiresAmount = rectificationActionRequiresAmount(selectedAction);
    const requiresPreview = rectificationActionRequiresPreview(selectedAction);
    const amountNumber = amount !== '' ? Number(amount) : null;
    const replacementTotalPriceNumber = replacementTotalPrice !== '' ? Number(replacementTotalPrice) : null;
    const usesExpirationSelection = requiresAmount && amountSource === 'expirations';
    const selectedSelectableExpirationIds = selectedExpirationIds.filter((expirationId) => (
        invoiceExpirations.some((expiration) => expiration.id === expirationId && !isExpirationPaid(expiration))
    ));
    const selectedExpirationAmount = invoiceExpirations
        .filter((expiration) => selectedSelectableExpirationIds.includes(expiration.id))
        .reduce((total, expiration) => total + getExpirationAmount(expiration), 0);
    const effectiveAmountNumber = usesExpirationSelection ? selectedExpirationAmount : amountNumber;

    useEffect(() => {
        if (!isOpen) return;

        setSelectedAction('minor_regeneration');
        setAmountSource('manual');
        setReason('');
        setAmount('');
        setReplacementTotalPrice('');
        setSelectedExpirationIds([]);
        setInvoiceExpirations([]);
        setOperationDate(moment().format('YYYY-MM-DD'));
        setPreview(null);
    }, [isOpen]);

    useEffect(() => {
        setPreview(null);
    }, [selectedAction, amount, replacementTotalPrice, amountSource, selectedExpirationIds]);

    useEffect(() => {
        if (!requiresAmount) {
            setAmountSource('manual');
            setSelectedExpirationIds([]);
        }
    }, [requiresAmount]);

    useEffect(() => {
        if (!usesExpirationSelection || !invoice?.id || invoiceExpirations.length > 0 || isPendingExpirationsLoading) return;

        const loadPendingExpirations = async () => {
            setIsPendingExpirationsLoading(true);
            try {
                const response = await service.previewCreditNote(invoice.id, null, null);
                const responseData = response.getResponseData();

                if (responseData?.success === false) {
                    handleErrors(responseData);
                    return;
                }

                const payload = getApiPayload(responseData);
                setInvoiceExpirations(getPreviewExpirations(payload));
            } catch (error) {
                handleErrors(error);
            } finally {
                setIsPendingExpirationsLoading(false);
            }
        };

        loadPendingExpirations();
    }, [handleErrors, invoice?.id, invoiceExpirations.length, isPendingExpirationsLoading, service, usesExpirationSelection]);

    const loadPreview = async () => {
        if (!invoice?.id) return;

        if (usesExpirationSelection && selectedSelectableExpirationIds.length === 0) {
            toast.error('Selecciona al menos un vencimiento para previsualizar el abono');
            return;
        }

        if (requiresAmount && !usesExpirationSelection && (!amountNumber || amountNumber <= 0)) {
            toast.error('Introduce un importe válido para previsualizar el abono');
            return;
        }

        setIsPreviewLoading(true);
        try {
            const response = await service.previewCreditNote(
                invoice.id,
                getCreditNotePreviewAmount(selectedAction, effectiveAmountNumber),
                usesExpirationSelection ? selectedSelectableExpirationIds : null
            );
            const responseData = response.getResponseData();

            if (responseData?.success === false) {
                handleErrors(responseData);
                return;
            }

            const payload = getApiPayload(responseData);
            setPreview(payload);
            setInvoiceExpirations(getPreviewExpirations(payload));
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsPreviewLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!invoice?.id) return;

        if (!reason.trim()) {
            toast.error('Indica el motivo de la operación');
            return;
        }

        if (usesExpirationSelection && selectedSelectableExpirationIds.length === 0) {
            toast.error('Selecciona al menos un vencimiento para el abono');
            return;
        }

        if (requiresAmount && (!effectiveAmountNumber || effectiveAmountNumber <= 0)) {
            toast.error('Introduce un importe válido para el abono');
            return;
        }

        if (selectedAction === 'major_replacement' && (!replacementTotalPriceNumber || replacementTotalPriceNumber <= 0)) {
            toast.error('Introduce un importe total válido para la nueva factura');
            return;
        }

        if (requiresPreview && !preview) {
            await loadPreview();
            return;
        }

        setIsSubmitting(true);
        try {
            let response;

            if (selectedAction === 'minor_regeneration') {
                response = await service.regenerateInvoice(invoice.id, reason, operationDate);
            } else if (selectedAction === 'full_credit_note') {
                response = await service.createFullCreditNote(invoice.id, reason, operationDate);
            } else if (selectedAction === 'partial_credit_note') {
                response = await service.createPartialCreditNote(
                    invoice.id,
                    reason,
                    effectiveAmountNumber,
                    operationDate,
                    usesExpirationSelection ? selectedSelectableExpirationIds : null
                );
            } else {
                response = await service.createCreditNoteAndReplacementInvoice(
                    invoice.id,
                    reason,
                    buildReplacementDataFromInvoice(invoice, replacementTotalPriceNumber),
                    operationDate,
                    effectiveAmountNumber,
                    usesExpirationSelection ? selectedSelectableExpirationIds : null
                );
            }

            const responseData = response.getResponseData();

            if (responseData?.success === false) {
                handleErrors(responseData);
                return;
            }

            toast.success(responseData?.message || 'Operación realizada correctamente');
            onCompleted?.();
            onClose();
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleToggleExpiration = (expiration: AffectedExpirationPreview, checked: boolean) => {
        if (isExpirationPaid(expiration)) return;

        const expirationId = expiration.id;

        setSelectedExpirationIds((current) => {
            if (checked) {
                return current.includes(expirationId) ? current : [...current, expirationId];
            }

            return current.filter((id) => id !== expirationId);
        });
    };

    const selectedActionData = ACTIONS.find((action) => action.value === selectedAction);

    return (
        <ModalForm isOpen={isOpen} onClose={onClose} title="Rectificar factura" size="xl">
            <div className="space-y-5">
                <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
                    <Text className="font-semibold text-gray-900">{invoice?.number}</Text>
                    <Text className="text-sm text-gray-600">
                        Total: {fixNumber(invoice?.totalPrice || 0)} € · Fecha: {invoice?.invoiceDate?.date ? moment(invoice.invoiceDate.date).format('DD/MM/YYYY') : '-'}
                    </Text>
                </div>

                <div>
                    <Text className="mb-2 text-sm font-semibold text-gray-800">Acción</Text>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
                        {ACTIONS.map((action) => (
                            <button
                                key={action.value}
                                type="button"
                                data-testid={`rectification-action-${action.value}`}
                                onClick={() => setSelectedAction(action.value)}
                                className={`min-h-[78px] rounded-md border px-3 py-2 text-left transition ${
                                    selectedAction === action.value
                                        ? 'border-primary bg-primary/5 text-primary'
                                        : 'border-gray-200 bg-white text-gray-700 hover:border-primary/60'
                                }`}
                            >
                                <span className="mb-1 flex items-center gap-2 text-sm font-semibold">
                                    {action.icon}
                                    {action.label}
                                </span>
                                <span className="block text-xs text-gray-500">{action.description}</span>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Input
                        label="Fecha de operación"
                        type="date"
                        value={operationDate}
                        onChange={(event) => setOperationDate(event.target.value)}
                    />

                    {requiresAmount && (
                        <div className="md:col-span-2">
                            <Text className="mb-2 text-sm font-semibold text-gray-800">Importe a abonar</Text>
                            <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-2">
                                <button
                                    type="button"
                                    onClick={() => setAmountSource('manual')}
                                    className={`rounded-md border px-3 py-2 text-left text-sm transition ${
                                        amountSource === 'manual'
                                            ? 'border-primary bg-primary/5 text-primary'
                                            : 'border-gray-200 bg-white text-gray-700 hover:border-primary/60'
                                    }`}
                                >
                                    Manual
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setAmountSource('expirations')}
                                    className={`rounded-md border px-3 py-2 text-left text-sm transition ${
                                        amountSource === 'expirations'
                                            ? 'border-primary bg-primary/5 text-primary'
                                            : 'border-gray-200 bg-white text-gray-700 hover:border-primary/60'
                                    }`}
                                >
                                    Seleccionar vencimientos
                                </button>
                            </div>

                            {amountSource === 'manual' ? (
                                <Input
                                    type="number"
                                    data-testid="rectification-amount"
                                    value={amount}
                                    onChange={(event) => setAmount(event.target.value)}
                                    suffix="€"
                                />
                            ) : (
                                <div className="rounded-md border border-gray-200">
                                    <div className="flex flex-col gap-1 border-b border-gray-100 bg-gray-50 px-3 py-2 md:flex-row md:items-center md:justify-between">
                                        <Text className="text-sm font-semibold text-gray-800">Vencimientos de la factura</Text>
                                        <Text className="text-sm text-gray-600">
                                            Seleccionado: {fixNumber(selectedExpirationAmount)} €
                                        </Text>
                                    </div>

                                    {isPendingExpirationsLoading ? (
                                        <Text className="px-3 py-4 text-sm text-gray-500">Cargando vencimientos...</Text>
                                    ) : invoiceExpirations.length === 0 ? (
                                        <Text className="px-3 py-4 text-sm text-gray-500">No hay vencimientos disponibles.</Text>
                                    ) : (
                                        <div className="max-h-[240px] overflow-y-auto">
                                            {invoiceExpirations.map((expiration) => (
                                                <label
                                                    key={expiration.id}
                                                    className={getExpirationSelectionRowClassName(expiration)}
                                                    aria-disabled={isExpirationPaid(expiration)}
                                                >
                                                    <Checkbox
                                                        checked={selectedExpirationIds.includes(expiration.id)}
                                                        disabled={isExpirationPaid(expiration)}
                                                        onChange={(event) => handleToggleExpiration(expiration, event.target.checked)}
                                                    />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex min-w-0 items-center gap-2">
                                                            <ExpirationInfoPopover expiration={expiration} />
                                                            <Text className="min-w-0 truncate text-sm font-semibold text-gray-800">
                                                                {expiration.description || expiration.id}
                                                            </Text>
                                                            <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${getExpirationPaymentBadgeClassName(expiration)}`}>
                                                                {getExpirationPaymentLabel(expiration)}
                                                            </span>
                                                        </div>
                                                        <Text className="text-xs text-gray-500">
                                                            {formatPreviewDate(expiration.expirationDate)} · {fixNumber(getExpirationAmount(expiration))} €
                                                        </Text>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {selectedAction === 'major_replacement' && (
                        <Input
                            label="Importe total nueva factura"
                            type="number"
                            data-testid="replacement-total-price"
                            value={replacementTotalPrice}
                            onChange={(event) => setReplacementTotalPrice(event.target.value)}
                            suffix="€"
                        />
                    )}
                </div>

                <Textarea
                    label="Motivo"
                    data-testid="rectification-reason"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    rows={4}
                />

                {selectedAction === 'major_replacement' && (
                    <div className="rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-900">
                        Se generará un abono completo y una factura sustitutiva con el importe total indicado, IVA incluido.
                    </div>
                )}

                {requiresPreview && (
                    <div className="rounded-md border border-gray-200 p-4">
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                            <div>
                                <Text className="font-semibold text-gray-900">Preview de abono</Text>
                                <Text className="text-sm text-gray-500">{selectedActionData?.description}</Text>
                            </div>
                            <Button
                                variant="outline"
                                onClick={loadPreview}
                                isLoading={isPreviewLoading}
                                disabled={isSubmitting || isPendingExpirationsLoading}
                            >
                                Previsualizar
                            </Button>
                        </div>

                        {preview && (
                            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                                <div className="rounded-md bg-gray-50 p-3">
                                    <Text className="text-xs text-gray-500">Máximo abonable</Text>
                                    <Text className="font-semibold">{fixNumber(preview.maxRectifiableAmount || 0)} €</Text>
                                </div>
                                <div className="rounded-md bg-gray-50 p-3">
                                    <Text className="text-xs text-gray-500">Pendiente en vencimientos</Text>
                                    <Text className="font-semibold">{fixNumber(preview.pendingExpirationAmount || 0)} €</Text>
                                </div>
                                <div className="rounded-md bg-gray-50 p-3">
                                    <Text className="text-xs text-gray-500">Total abono</Text>
                                    <Text className="font-semibold">{fixNumber(Math.abs(preview.creditNote?.totalPrice || 0))} €</Text>
                                </div>
                            </div>
                        )}

                        {preview?.affectedExpirations && preview.affectedExpirations.length > 0 && (
                            <div className="mt-4 overflow-x-auto">
                                <table className="w-full min-w-[620px] text-left text-sm">
                                    <thead>
                                        <tr className="border-b text-gray-500">
                                            <th className="py-2">Vencimiento</th>
                                            <th className="py-2">Importe</th>
                                            <th className="py-2">Aplicado</th>
                                            <th className="py-2">Restante</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.affectedExpirations.map((expiration) => (
                                            <tr key={expiration.id} className="border-b last:border-b-0">
                                                <td className="py-2">
                                                    <div className="flex min-w-0 items-center gap-2">
                                                        <ExpirationInfoPopover expiration={expiration} />
                                                        <span className="truncate font-medium text-gray-800">
                                                            {expiration.description || expiration.id}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="py-2">{fixNumber(getExpirationAmount(expiration))} €</td>
                                                <td className="py-2">{fixNumber(expiration.appliedAmount)} €</td>
                                                <td className="py-2">{fixNumber(expiration.remainingAmount)} €</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                    <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
                        Cancelar
                    </Button>
                    <Button
                        color="primary"
                        data-testid="rectification-submit"
                        onClick={handleSubmit}
                        isLoading={isSubmitting}
                        disabled={isSubmitting || isPreviewLoading || isPendingExpirationsLoading}
                    >
                        {requiresPreview && !preview ? 'Previsualizar antes de confirmar' : 'Confirmar operación'}
                    </Button>
                </div>
            </div>
        </ModalForm>
    );
};

export default InvoiceRectificationModal;
